import { ITimeProvider } from "../hexagon/adapters/driven/providers/provider";
import {
  processHourlyData,
  processHourlyDataHR,
} from "../util/cdlDataProcessor/cdlDataProcessor";
import { useAppDispatch, useAppSelector } from "./redux";
import {
  removeStats,
  removeStatsHR,
  setStats,
  setStatsHR,
} from "../redux/Dashboard/dashboard.slice";
import { useEffect, useRef } from "react";
import chunk from "lodash/chunk";

export const useDashboardStats = ({
  cdlRepository,
  timeProvider,
}: {
  cdlRepository: ICDLRepository;
  timeProvider: ITimeProvider;
}) => {
  const controllersRef = useRef<AbortController[]>([]);
  const dispatch = useAppDispatch();

  const { stats, statsHR } = useAppSelector((state) => ({
    stats: state.dashboard.stats,
    statsHR: state.dashboard.statsHR,
  }));

  useEffect(() => {
    return () => {
      // Properly abort all pending requests to prevent memory leaks
      controllersRef.current.forEach((controller) => {
        try {
          controller.abort();
        } catch {
          // Ignore abort errors - controller might already be aborted
        }
      });
      controllersRef.current = [];
    };
  }, []);

  const getStatsData = async (patientIds: number[]) => {
    await getStatsData2(patientIds);
  };

  const getStatsDataHR = async (patientIds: number[]) => {
    await getStatsDataHR2(patientIds);
  };

  const filterOutAlreadyExisting = (patientIds: number[]) => {
    return patientIds.filter((item) => {
      return !Object.hasOwn(stats, item) || stats[item].avg === null;
    });
  };

  const getStatsData2 = async (patientIds: number[]) => {
    const ids = filterOutAlreadyExisting(patientIds);
    if (ids.length === 0) return;
    const chunks = chunk(ids, 50);

    for (const idsArr of chunks) {
      const localController = new AbortController();
      controllersRef.current.push(localController);

      const response =
        await cdlRepository.getPatientHourlyRRWithoutProcessingV2({
          patient_ids: idsArr,
          date_from: timeProvider
            .getDateTime()
            .subtract(168, "hours")
            .format("YYYY-MM-DDTHH:mm:ssZ"),
          date_to: timeProvider.getDateTime().format("YYYY-MM-DDTHH:mm:ssZ"),
          signal: localController.signal,
        });

      if (!response?.data) {
        continue;
      }

      const promisesArray = Object.entries(response.data).map(([k, v]) => ({
        patientId: Number(k),
        data: v,
      }));

      const data = await processHourlyData(promisesArray);

      const newStatsData: {
        [key: string]: { avg: number | null; date: string | null };
      } = {};

      for (let i = 0; i < data.length; i++) {
        const daysRange = data[i].data;
        const returnedPatientId = data[i].patientId;
        newStatsData[returnedPatientId] = { avg: null, date: null };

        const numArr = Object.entries(daysRange).filter(([, v]) => {
          return !isNaN(v as number);
        });
        if (numArr.length > 0) {
          const lastEntry = numArr[numArr.length - 1];
          newStatsData[returnedPatientId] = {
            avg: lastEntry[1] as number,
            date: lastEntry[0],
          };
        }
      }

      dispatch(
        setStats({
          value: newStatsData,
          timeout: timeProvider.dateLibrary().unix(),
        })
      );

      // Clean up completed controller to prevent memory accumulation
      const controllerIndex = controllersRef.current.indexOf(localController);
      if (controllerIndex > -1) {
        controllersRef.current.splice(controllerIndex, 1);
      }
    }
  };

  const filterOutAlreadyExistingHR = (patientIds: number[]) => {
    return patientIds.filter((item) => {
      return !Object.hasOwn(statsHR, item) || statsHR[item].avg === null;
    });
  };

  const getStatsDataHR2 = async (patientIds: number[]) => {
    const ids = filterOutAlreadyExistingHR(patientIds);
    if (ids.length === 0) return;

    const chunks = chunk(ids, 50);

    for (const idsArr of chunks) {
      const localController = new AbortController();
      controllersRef.current.push(localController);

      const response =
        await cdlRepository.getPatientHourlyHRWithoutProcessingV2({
          patient_ids: idsArr,
          date_from: timeProvider
            .getDateTime()
            .subtract(168, "hours")
            .format("YYYY-MM-DDTHH:mm:ssZ"),
          date_to: timeProvider.getDateTime().format("YYYY-MM-DDTHH:mm:ssZ"),
          signal: localController.signal,
        });

      if (!response?.data) {
        continue;
      }

      const promisesArray = Object.entries(response.data).map(([k, v]) => ({
        patientId: Number(k),
        data: v,
      }));

      const data = await processHourlyDataHR(promisesArray);

      const newStatsData: {
        [key: string]: { avg: number | null; date: string | null };
      } = {};

      for (let i = 0; i < data.length; i++) {
        const daysRange = data[i].data;
        const returnedPatientId = data[i].patientId;
        newStatsData[returnedPatientId] = { avg: null, date: null };

        const numArr = Object.entries(daysRange).filter(([, v]) => {
          return !isNaN(v as number);
        });
        if (numArr.length > 0) {
          const lastEntry = numArr[numArr.length - 1];
          newStatsData[returnedPatientId] = {
            avg: lastEntry[1] as number,
            date: lastEntry[0],
          };
        }
      }

      dispatch(
        setStatsHR({
          value: newStatsData,
          timeout: timeProvider.dateLibrary().unix(),
        })
      );

      // Clean up completed controller to prevent memory accumulation
      const controllerIndex = controllersRef.current.indexOf(localController);
      if (controllerIndex > -1) {
        controllersRef.current.splice(controllerIndex, 1);
      }
    }
  };

  const removeStatsData = (residentIds?: number[]) => {
    dispatch(removeStats(residentIds));
  };

  const removeStatsDataHR = (residentIds?: number[]) => {
    dispatch(removeStatsHR(residentIds));
  };

  return {
    stats,
    getStatsData,
    removeStatsData,
    statsHR,
    getStatsDataHR,
    removeStatsDataHR,
  };
};
